import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  CheckCircle2, XCircle, Download, File, Archive, ExternalLink,
  Terminal, Globe, Code2, BarChart3, FileEdit, Zap, ChevronDown, ChevronUp,
  MessageSquare, ChevronRight, Circle, CalendarDays, FileText
} from 'lucide-react'
import type {
  Message,
  AgentTask,
  AgentFile,
  AgentStructuredOutput,
  AgentMemorySummary,
  AgentWorkflowSummary,
  AgentSemanticEvent,
  AgentSubagentRuntime,
} from '@/types'
import type { AgentLogEntry, PlanningTask } from '@/hooks/useAgent'
import { RecurringTaskCard } from './RecurringTaskCard'
import { SineThinkingLoader } from '@/components/ui/SineThinkingLoader'

interface AgentChatMessageProps {
  message: Message
  liveLogs?: AgentLogEntry[]
  planningTasks?: PlanningTask[]
  onOpenFile?: (file: AgentFile, allFiles?: AgentFile[]) => void
  onSuggestion?: (text: string) => void
}

function semanticIcon(kind: AgentSemanticEvent['kind']) {
  if (kind === 'browser') return <Globe size={13} />
  if (kind === 'terminal') return <Terminal size={13} />
  if (kind === 'file') return <FileText size={13} />
  if (kind === 'warning') return <Zap size={13} />
  if (kind === 'done') return <CheckCircle2 size={13} />
  return <Circle size={10} />
}

function isUsefulSemanticEvent(event: AgentSemanticEvent, omitSetup: boolean): boolean {
  const text = event.text.trim()
  if (!text) return false
  const normalized = text.toLowerCase()
  if (omitSetup && (
    normalized.includes('jeg leser oppgaven og velger')
    || normalized.includes('jeg deler dette opp i')
    || normalized.includes('planen er klar')
  )) return false
  if (/^(kjører|søker:|web_search|browser|terminal|llm-feil|advarsel:)/i.test(text)) return false
  return true
}

function dedupeSemanticEvents(events: AgentSemanticEvent[]): AgentSemanticEvent[] {
  const seen = new Set<string>()
  return [...events].reverse().filter(event => {
    const key = `${event.kind}:${event.title ?? ''}:${event.text}`.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  }).reverse()
}

function SemanticEventStream({
  events,
  running,
  compact = false,
  omitSetup = false,
}: {
  events: AgentSemanticEvent[]
  running: boolean
  compact?: boolean
  omitSetup?: boolean
}) {
  const visibleEvents = dedupeSemanticEvents(events)
    .filter(event => isUsefulSemanticEvent(event, omitSetup))
    .slice(compact ? (running ? -4 : -3) : (running ? -8 : -4))

  if (visibleEvents.length === 0) return null

  return (
    <div className={`agent-semantic-stream${compact ? ' agent-semantic-stream--compact' : ''}`}>
      {visibleEvents.map((event) => (
        <div key={event.id} className={`agent-semantic-event is-${event.kind} status-${event.status ?? 'info'}`}>
          <span className="agent-semantic-icon">{semanticIcon(event.kind)}</span>
          <div className="agent-semantic-copy">
            {event.title && <div className="agent-semantic-title">{event.title}</div>}
            <div className="agent-semantic-text">{event.text}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

function toolHintLabel(toolHint?: string, lane?: string): string {
  if (lane === 'briefing' && toolHint === 'web_search') return 'Kontekstsøk'
  if (lane === 'briefing' && toolHint === 'browser') return 'Systemoppslag'
  if (lane === 'report' && toolHint === 'write_file') return 'Rapportutkast'
  if (lane === 'report' && toolHint === 'read_file') return 'Datagrunnlag'
  if (lane === 'monitoring' && toolHint === 'browser') return 'Signal-sjekk'
  if (lane === 'monitoring' && toolHint === 'write_file') return 'Oppsett'
  if (lane === 'incident' && toolHint === 'browser') return 'Reproduksjon'
  if (lane === 'incident' && toolHint === 'terminal') return 'Kode/terminal'
  const labels: Record<string, string> = {
    terminal: 'Terminal',
    web_search: 'Web-søk',
    write_file: 'Skrive fil',
    read_file: 'Lese fil',
    browser: 'Nettleser',
    git_clone: 'Git',
    none: 'Åpen strategi',
  }
  return labels[toolHint ?? 'none'] ?? toolHint ?? 'Åpen strategi'
}

function toolHintDescription(toolHint?: string, lane?: string): string {
  if (lane === 'briefing') {
    const descriptions: Record<string, string> = {
      terminal: 'Brukes hvis briefingen krever lokal bearbeiding eller skript.',
      web_search: 'Henter fersk kontekst og signaler som styrker briefingen.',
      write_file: 'Gjør briefingen klar som artefakt eller notat.',
      read_file: 'Leser inn dokumenter, notater eller tidligere kontekst.',
      browser: 'Slår opp i dashboards, nettsystemer eller live kilder.',
      git_clone: 'Henter repo-kontekst hvis briefingen berører kode.',
      none: 'Agenten holder briefingen åpen til mer kontekst er tydelig.',
    }
    return descriptions[toolHint ?? 'none'] ?? 'Agenten vurderer beste vei for å bygge briefingen.'
  }

  if (lane === 'report') {
    const descriptions: Record<string, string> = {
      terminal: 'Brukes hvis rapporten krever lokal transformasjon eller analyse.',
      web_search: 'Henter eksterne tall, research eller ferske signaler.',
      write_file: 'Skriver selve rapporten eller et leveranseutkast.',
      read_file: 'Leser datagrunnlag, filer eller tidligere rapporter.',
      browser: 'Henter informasjon fra dashboards, nettsider eller live systemer.',
      git_clone: 'Henter repo- eller kodekontekst som del av rapporten.',
      none: 'Agenten holder rapportstrategien åpen mens grunnlaget formes.',
    }
    return descriptions[toolHint ?? 'none'] ?? 'Agenten vurderer beste vei for å bygge rapporten.'
  }

  if (lane === 'monitoring') {
    const descriptions: Record<string, string> = {
      terminal: 'Brukes hvis monitoren trenger lokal behandling eller skript.',
      web_search: 'Henter eksterne signaler monitoren skal følge med på.',
      write_file: 'Formulerer oppsett, regler eller leveranse for monitoren.',
      read_file: 'Leser eksisterende oppsett, dokumenter eller signalgrunnlag.',
      browser: 'Sjekker nettsider, dashboards eller visuelle signaler.',
      git_clone: 'Henter repo-kontekst hvis monitoren berører kode eller deploy.',
      none: 'Agenten holder monitoroppsettet åpent mens detaljene avklares.',
    }
    return descriptions[toolHint ?? 'none'] ?? 'Agenten vurderer beste vei for å sette opp monitoren.'
  }

  if (lane === 'incident') {
    const descriptions: Record<string, string> = {
      terminal: 'Brukes for kode, logs eller lokal reproduksjon av feilen.',
      web_search: 'Henter ekstern feilkontekst eller ferske statusdata.',
      write_file: 'Dokumenterer funn, tiltak eller oppfølging.',
      read_file: 'Leser kode, konfig eller artefakter for å forstå feilen.',
      browser: 'Prøver å reprodusere eller inspisere problemet i nettleser.',
      git_clone: 'Henter repo eller kodebase for raskere feilsøking.',
      none: 'Agenten holder første incident-spor åpent mens signalene samles.',
    }
    return descriptions[toolHint ?? 'none'] ?? 'Agenten vurderer beste vei for å angripe incidenten.'
  }

  const descriptions: Record<string, string> = {
    terminal: 'Trolig kode, kommandoer eller lokal kjøring.',
    web_search: 'Trolig research, oppslag eller fersk informasjon.',
    write_file: 'Trolig artefakt, rapport eller filproduksjon.',
    read_file: 'Trolig lesing av eksisterende filer eller dokumenter.',
    browser: 'Trolig nettsider, dashboards eller visuell kontroll.',
    git_clone: 'Trolig repo- eller kodebasekontekst.',
    none: 'Agenten holder steget mer åpent foreløpig.',
  }
  return descriptions[toolHint ?? 'none'] ?? 'Agenten vurderer riktig verktøy for dette steget.'
}

function _PlanningTasksCard({ tasks, lane }: { tasks: PlanningTask[]; lane?: string }) {
  if (tasks.length === 0) return null

  const laneMeta: Record<string, { title: string; subtitle: string; accent: string; border: string; bg: string }> = {
    briefing: {
      title: 'Briefing-plan',
      subtitle: 'Sine samler kontekst og skjerper det viktigste før svaret lander.',
      accent: '#93C5FD',
      border: 'rgba(96,165,250,0.18)',
      bg: 'rgba(96,165,250,0.06)',
    },
    report: {
      title: 'Rapportplan',
      subtitle: 'Sine bygger datagrunnlag, struktur og leveranse i riktig rekkefølge.',
      accent: '#A7F3D0',
      border: 'rgba(16,185,129,0.18)',
      bg: 'rgba(16,185,129,0.05)',
    },
    monitoring: {
      title: 'Monitor-oppsett',
      subtitle: 'Sine definerer hva som skal sjekkes, hvordan det følges opp og når det varsles.',
      accent: '#FCD34D',
      border: 'rgba(245,158,11,0.2)',
      bg: 'rgba(245,158,11,0.05)',
    },
    incident: {
      title: 'Incident-plan',
      subtitle: 'Sine samler signaler, snevrer inn årsaken og peker på neste operasjonelle trekk.',
      accent: '#FCA5A5',
      border: 'rgba(248,113,113,0.2)',
      bg: 'rgba(248,113,113,0.05)',
    },
    coordination: {
      title: 'Koordineringsplan',
      subtitle: 'Sine avklarer tid, kanal og neste handling på tvers av systemer.',
      accent: '#C4B5FD',
      border: 'rgba(139,92,246,0.18)',
      bg: 'rgba(139,92,246,0.05)',
    },
    default: {
      title: 'Plan',
      subtitle: 'Sine bryter oppgaven ned i konkrete steg før utførelsen starter.',
      accent: '#D1D5DB',
      border: 'rgba(255,255,255,0.08)',
      bg: 'rgba(255,255,255,0.03)',
    },
  }
  const meta = laneMeta[lane ?? 'default'] ?? laneMeta.default

  return (
    <div style={{
      marginBottom: 16,
      padding: '14px 14px 12px',
      borderRadius: 14,
      border: `1px solid ${meta.border}`,
      background: meta.bg,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 6,
        color: meta.accent,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '0.02em',
      }}>
        <span>{meta.title}</span>
      </div>
      <div style={{ fontSize: 12, color: '#A3A3A3', lineHeight: 1.55, marginBottom: 12 }}>
        {meta.subtitle}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {tasks.map((task, index) => {
          const active = task.status === 'active'
          const done = task.status === 'done'
          const error = task.status === 'error'

          return (
            <div key={`${task.id ?? index}-${task.label}`} style={{
              padding: '10px 11px',
              borderRadius: 12,
              border: active
                ? '1px solid rgba(96,165,250,0.24)'
                : done
                  ? '1px solid rgba(74,222,128,0.18)'
                  : error
                    ? '1px solid rgba(248,113,113,0.22)'
                    : '1px solid rgba(255,255,255,0.06)',
              background: active
                ? 'rgba(96,165,250,0.08)'
                : done
                  ? 'rgba(74,222,128,0.05)'
                  : error
                    ? 'rgba(248,113,113,0.06)'
                    : 'rgba(255,255,255,0.02)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  color: done ? '#4ADE80' : error ? '#F87171' : active ? '#93C5FD' : '#9CA3AF',
                  background: 'rgba(255,255,255,0.05)',
                  flexShrink: 0,
                }}>
                  {done ? '✓' : index + 1}
                </span>
                <div style={{ fontSize: 13, color: '#F3F4F6', fontWeight: 600, lineHeight: 1.45 }}>
                  {task.label}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: 11,
                  color: active ? '#BFDBFE' : '#D1D5DB',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 999,
                  padding: '3px 8px',
                }}>
                  {toolHintLabel(task.tool_hint, lane)}
                </span>
                <span style={{ fontSize: 12, color: '#9CA3AF', lineHeight: 1.5 }}>
                  {toolHintDescription(task.tool_hint, lane)}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── File icon ────────────────────────────────────────────────
function ManusPlanningTasksCard({ tasks, lane }: { tasks: PlanningTask[]; lane?: string }) {
  if (tasks.length === 0) return null

  const laneCopy: Record<string, { title: string; subtitle: string }> = {
    briefing: {
      title: 'Jeg lager en presis briefing-løype.',
      subtitle: 'Først henter jeg kontekst, så skiller jeg signaler fra støy og avslutter med en kort, brukbar oppsummering.',
    },
    report: {
      title: 'Jeg bygger rapporten i riktig rekkefølge.',
      subtitle: 'Jeg starter med kilder og tallgrunnlag, sjekker funnene mot hverandre og skriver leveransen først når grunnlaget er godt nok.',
    },
    monitoring: {
      title: 'Jeg setter opp dette som en trygg monitor.',
      subtitle: 'Jeg definerer hva som skal følges med på, hvordan det sjekkes og hva som skal skje når noe endrer seg.',
    },
    incident: {
      title: 'Jeg feilsøker dette systematisk.',
      subtitle: 'Jeg samler symptomer, finner sannsynlig rotårsak og tester små, reversible tiltak før jeg konkluderer.',
    },
    coordination: {
      title: 'Jeg koordinerer dette steg for steg.',
      subtitle: 'Jeg finner hvem, hva, når og kanal før jeg foreslår eller utfører neste handling.',
    },
    default: {
      title: 'Jeg har planen klar.',
      subtitle: 'Jeg deler oppgaven i konkrete steg og viser hva jeg gjør underveis, uten å fylle chatten med rå teknisk støy.',
    },
  }
  const copy = laneCopy[lane ?? 'default'] ?? laneCopy.default
  const activeIndex = tasks.findIndex(task => task.status === 'active')
  const lastDoneIndex = tasks.reduce((latest, task, index) => task.status === 'done' ? index : latest, -1)
  const allDone = tasks.every(task => task.status === 'done')
  const visibleUntil = allDone
    ? tasks.length - 1
    : Math.max(0, activeIndex >= 0 ? activeIndex : lastDoneIndex + 1)
  const visibleTasks = tasks.slice(0, visibleUntil + 1)

  return (
    <div className="agent-narrative-plan">
      <div className="agent-narrative__intro">
        <p><strong>{copy.title}</strong></p>
        <p>{copy.subtitle}</p>
      </div>

      <div className="agent-task-sequence">
        {visibleTasks.map((task, index) => {
          const done = task.status === 'done'
          const active = task.status === 'active'
          const error = task.status === 'error'
          const isLast = index === visibleTasks.length - 1
          return (
            <div
              key={`${task.id ?? index}-${task.label}`}
              className={[
                'agent-task-step',
                done ? 'agent-task-step--done' : '',
                active ? 'agent-task-step--active' : '',
                error ? 'agent-task-step--error' : '',
              ].filter(Boolean).join(' ')}
            >
              <div className="agent-task-step__rail">
                <div className="agent-task-step__check">
                  {done ? <CheckCircle2 size={12} /> : error ? <XCircle size={12} /> : index + 1}
                </div>
                {!isLast && <div className="agent-task-step__line" />}
              </div>

              <div className="agent-task-step__body">
                <div className="agent-task-step__title-row">
                  <div className="agent-task-step__title">{task.label}</div>
                  <span className="agent-task-step__count">{toolHintLabel(task.tool_hint, lane)}</span>
                </div>

                <p className="agent-task-step__feedback">
                  {task.prose || toolHintDescription(task.tool_hint, lane)}
                </p>

                {task.chips.length > 0 && (
                  <div className="agent-task-step__chips">
                    {task.chips.map(chip => {
                      const meta = getToolMeta(chip.tool)
                      return (
                        <span
                          key={chip.id}
                          className={[
                            'agent-action-chip',
                            chip.success === true ? 'is-done' : '',
                            chip.success === false ? 'is-error' : '',
                          ].filter(Boolean).join(' ')}
                        >
                          <span className="agent-action-chip__icon">{meta.icon}</span>
                          <span className="agent-action-chip__label">{chip.label}</span>
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function subagentRoleLabel(role: string): string {
  const normalized = role.replaceAll('_', ' ').trim()
  const labels: Record<string, string> = {
    researcher: 'Research',
    browser: 'Nettleser',
    writer: 'Skriving',
    analyst: 'Analyse',
    verifier: 'Kvalitetssjekk',
    developer: 'Kode',
    agent: 'Agent',
  }
  return labels[role] ?? (normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Agent')
}

function cleanRuntimeCopy(value?: string | null): string {
  if (!value) return ''

  const withoutMarkdown = value
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*/g, '')
    .replace(/^#+\s*/gm, '')
    .replace(/\|[-:|\s]+\|/g, ' ')
    .replace(/\|/g, ' ')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/https?:\/\/\S+/g, 'nettkilde')

  const normalized = withoutMarkdown
    .replace(/Søkefeil:?.*?(?=\.|$)/gi, 'Jeg byttet kilde fordi et søk ble begrenset')
    .replace(/rate\s*limit.*?(?=\.|$)/gi, 'Jeg byttet kilde fordi et søk ble begrenset')
    .replace(/command exited with code \d+.*?(?=\.|$)/gi, 'Et teknisk forsøk ga ikke nyttig resultat')
    .replace(/deploy[-\s]?preview.*?(?=\.|$)/gi, 'Forhåndsvisningen brukte for lang tid')
    .replace(/\s+/g, ' ')
    .trim()

  if (!normalized) return ''
  return normalized.length > 132 ? `${normalized.slice(0, 129).trim()}...` : normalized
}

function cleanVisibleAgentContent(value: string): string {
  if (!value) return ''
  const strippedLines = value
    .split('\n')
    .filter(line => {
      const trimmed = line.trim()
      if (!trimmed) return true
      if (/^(web_search|browser|terminal|deploy_preview|run_code)\s*:/i.test(trimmed)) return false
      if (/^(Søkefeil|SÃ¸kefeil|E2B terminal-feil|Command exited with code|Deploy\/preview feilet)/i.test(trimmed)) return false
      if (/https?:\/\/(html|lite)\.duckduckgo\.com/i.test(trimmed)) return false
      return true
    })
    .join('\n')
  return strippedLines
    .replace(/Jeg har fortsatt prÃ¸vd Ã¥ levere det jeg kunne/g, 'Jeg har fortsatt prøvd å levere det jeg kunne')
    .replace(/forsÃ¸k/g, 'forsøk')
    .replace(/fullfÃ¸re/g, 'fullføre')
    .replace(/kjÃ¸ringen/g, 'kjøringen')
    .replace(/pÃ¥/g, 'på')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function runtimeStatusLabel(status: AgentSubagentRuntime['status']): string {
  if (status === 'queued') return 'venter'
  if (status === 'running') return 'jobber'
  if (status === 'done') return 'ferdig'
  if (status === 'fallback') return 'ny strategi'
  return 'forbehold'
}

function runtimeHumanSummary(agent: AgentSubagentRuntime): string {
  if (agent.status === 'queued') return 'Venter til denne delen er klar til å kjøres.'
  if (agent.status === 'running') {
    return cleanRuntimeCopy(agent.summary || agent.task) || 'Jobber med denne delen i bakgrunnen.'
  }
  if (agent.status === 'done') {
    return cleanRuntimeCopy(agent.summary || agent.task) || 'Denne delen er ferdig og funnene brukes videre.'
  }
  if (agent.status === 'fallback') {
    return cleanRuntimeCopy(agent.warning || agent.summary) || 'Sine byttet strategi og fortsetter med en tryggere vei.'
  }
  return cleanRuntimeCopy(agent.warning || agent.summary) || 'Denne delen ga ikke nok signal, men resten av oppgaven fortsetter.'
}

function SubagentRuntimePanel({ agents, running }: { agents: AgentSubagentRuntime[]; running: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const visibleAgents = agents
    .filter(agent => agent.task.trim().length > 0)
    .slice(-6)

  if (visibleAgents.length === 0) return null

  const completed = visibleAgents.filter(agent => agent.status === 'done').length
  const pending = visibleAgents.filter(agent => agent.status === 'queued' || agent.status === 'running').length
  const active = visibleAgents.find(agent => agent.status === 'running')
  const warningCount = visibleAgents.filter(agent => agent.status === 'fallback' || agent.status === 'failed').length
  const headline = running
    ? pending > 1 ? `${pending} deler jobber parallelt` : active ? `${subagentRoleLabel(active.role)} jobber` : 'Sine jobber parallelt'
    : warningCount > 0 ? 'Grunnarbeid samlet med forbehold' : 'Grunnarbeid samlet'

  return (
    <div className="agent-runtime-panel">
      <button
        type="button"
        className="agent-runtime-panel__header"
        onClick={() => setExpanded(value => !value)}
      >
        <span className="agent-runtime-panel__title">
          {headline}
          {active && <span className="agent-runtime-panel__active">Nå: {subagentRoleLabel(active.role)}</span>}
        </span>
        <span className="agent-runtime-panel__meta">{completed}/{visibleAgents.length}</span>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {!expanded && (
        <div className="agent-runtime-pill-row" aria-hidden="true">
          {visibleAgents.slice(0, 4).map(agent => (
            <span key={agent.id} className={`agent-runtime-pill is-${agent.status}`}>
              {subagentRoleLabel(agent.role)}
            </span>
          ))}
        </div>
      )}

      {expanded && (
        <div className="agent-runtime-list">
          {visibleAgents.map(agent => (
            <div key={agent.id} className={`agent-runtime-row is-${agent.status}`}>
              <span className="agent-runtime-dot" />
              <div className="agent-runtime-copy">
                <div className="agent-runtime-title">
                  <span>{subagentRoleLabel(agent.role)}</span>
                  <span className="agent-runtime-status">{runtimeStatusLabel(agent.status)}</span>
                </div>
                <p>{runtimeHumanSummary(agent)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function getFileIcon(type: AgentFile['type']) {
  if (type === 'archive') {
    return (
      <img src="/fil.svg" alt="Arkiv"
        style={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }} />
    )
  }
  return (
    <img src="/dokument.svg" alt="Dokument"
      style={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }} />
  )
}

// ─── File type from filename ──────────────────────────────────
export function getFileType(filename: string): AgentFile['type'] {
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''
  if (['md', 'mdx'].includes(ext)) return 'markdown'
  if (ext === 'csv') return 'csv'
  if (ext === 'json') return 'json'
  if (['html', 'htm'].includes(ext)) return 'html'
  if (ext === 'pdf') return 'pdf'
  if (['doc', 'docx'].includes(ext)) return 'document'
  if (['xls', 'xlsx'].includes(ext)) return 'spreadsheet'
  if (['js', 'ts', 'tsx', 'jsx', 'py', 'rs', 'go', 'java', 'css', 'yaml', 'yml', 'sh', 'bash'].includes(ext)) return 'code'
  if (['zip', 'tar', 'gz', 'rar', '7z'].includes(ext)) return 'archive'
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)) return 'image'
  if (['txt', 'log'].includes(ext)) return 'text'
  return 'other'
}

// ─── Tool icon + label ────────────────────────────────────────
function getToolMeta(tool: string): { icon: React.ReactNode; label: string } {
  switch (tool) {
    case 'terminal':
      return { icon: <Terminal size={12} />, label: tool }
    case 'web_search':
      return { icon: <Globe size={12} />, label: tool }
    case 'gmail_read':
    case 'gmail_send':
      return { icon: <MessageSquare size={12} />, label: tool }
    case 'github_list_repos':
    case 'github_read_file':
    case 'github_push_file':
    case 'github_create_repo':
      return { icon: <Code2 size={12} />, label: tool }
    case 'deploy_preview':
      return { icon: <ExternalLink size={12} />, label: tool }
    case 'mcp_list_tools':
    case 'mcp_call_tool':
      return { icon: <Zap size={12} />, label: tool }
    case 'write_file':
      return { icon: <FileEdit size={12} />, label: tool }
    case 'read_file':
      return { icon: <FileEdit size={12} />, label: tool }
    case 'run_code':
      return { icon: <Code2 size={12} />, label: tool }
    case 'analyze_data':
      return { icon: <BarChart3 size={12} />, label: tool }
    case 'list_files':
      return { icon: <File size={12} />, label: tool }
    default:
      return { icon: <Zap size={12} />, label: tool }
  }
}

function getLaneAwarePillLabel(task: AgentTask, lane?: string): string {
  const tool = task.tool ?? ''

  if (lane === 'briefing') {
    if (tool === 'gmail_read') return 'Henter e-postkontekst'
    if (tool === 'browser' || tool === 'web_search') return 'Henter fersk kontekst'
    if (tool === 'read_file') return 'Leser bakgrunnsmateriale'
  }

  if (lane === 'report') {
    if (tool === 'write_file') return 'Former rapportutkast'
    if (tool === 'read_file') return 'Leser datagrunnlag'
    if (tool === 'browser' || tool === 'web_search') return 'Henter tall og signaler'
  }

  if (lane === 'monitoring') {
    if (tool === 'browser' || tool === 'web_search') return 'Sjekker signaler'
    if (tool === 'write_file') return 'Setter opp monitoren'
    if (tool === 'gmail_send') return 'Klargjør varsling'
  }

  if (lane === 'incident') {
    if (tool === 'browser') return 'Prøver å reprodusere feilen'
    if (tool === 'github_read_file' || tool === 'github_list_repos') return 'Leser kodekontekst'
    if (tool === 'terminal') return 'Undersøker kode og logs'
  }

  return task.label
}

function getLaneAwareLiveStatus(entry: AgentLogEntry, lane?: string): string {
  if (entry.type === 'tool_call') {
    const tool = entry.tool ?? ''
    if (lane === 'briefing') {
      if (tool === 'gmail_read') return 'Henter relevant e-post og meldingskontekst…'
      if (tool === 'browser' || tool === 'web_search') return 'Samler fersk briefing-kontekst…'
    }
    if (lane === 'report') {
      if (tool === 'read_file' || tool === 'browser' || tool === 'web_search') return 'Samler datagrunnlag til rapporten…'
      if (tool === 'write_file') return 'Strukturerer og skriver rapporten…'
    }
    if (lane === 'monitoring') {
      if (tool === 'browser' || tool === 'web_search') return 'Sjekker hvilke signaler monitoren skal følge…'
      if (tool === 'write_file') return 'Former oppsettet for monitoren…'
    }
    if (lane === 'incident') {
      if (tool === 'browser') return 'Prøver å reprodusere problemet…'
      if (tool === 'github_read_file' || tool === 'github_list_repos') return 'Sjekker kode, diff og repo-kontekst…'
      if (tool === 'terminal') return 'Undersøker logs og kode lokalt…'
    }
    return `Kjører ${entry.tool ?? entry.message}`
  }

  if (entry.type === 'tool_result') {
    return entry.success === false ? `Feil: ${entry.message}` : ''
  }

  if (entry.type === 'thinking' || entry.type === 'log') {
    return entry.message.length > 160 ? entry.message.slice(0, 160) + '…' : entry.message
  }

  return ''
}

// ─── Action pill (Manus-style) ────────────────────────────────
function ActionPill({ task, lane }: { task: AgentTask; lane?: string }) {
  const isRunning = task.status === 'running'
  const isError = task.status === 'error'
  const meta = getToolMeta(task.tool ?? 'other')

  return (
    <div className={`mns-pill${isRunning ? ' mns-pill-running' : ''}${isError ? ' mns-pill-error' : ''}`}>
      {isRunning && <div className="mns-pill-shimmer" />}
      <span className="mns-pill-icon">{meta.icon}</span>
      <span className="mns-pill-text">{getLaneAwarePillLabel(task, lane)}</span>
    </div>
  )
}

// ─── Phase step (Manus-style) ─────────────────────────────────
interface PhaseStep {
  title: string
  description: string
  pills: AgentTask[]
  status: 'done' | 'running' | 'pending' | 'error'
  isLast: boolean
}

function PhaseStepView({ step, lane }: { step: PhaseStep; lane?: string }) {
  const [expanded, setExpanded] = useState(true)
  const isDone = step.status === 'done'
  const isRunning = step.status === 'running'
  const isError = step.status === 'error'

  return (
    <div className="mns-phase">
      {/* Left column: checkmark + dashed line */}
      <div className="mns-phase-left">
        <div className={`mns-phase-check${isDone ? ' done' : ''}${isRunning ? ' running' : ''}${isError ? ' error' : ''}`}>
          {isDone && <CheckCircle2 size={14} />}
          {isRunning && <div className="mns-phase-spinner" />}
          {isError && <XCircle size={14} />}
          {step.status === 'pending' && <Circle size={14} />}
        </div>
        {!step.isLast && <div className="mns-phase-line" />}
      </div>

      {/* Right column: title + description + pills */}
      <div className="mns-phase-body">
        <button
          className="mns-phase-title-row"
          onClick={() => setExpanded(e => !e)}
        >
          <span className="mns-phase-title">{step.title}</span>
          {expanded
            ? <ChevronDown size={13} className="mns-phase-chevron" />
            : <ChevronUp size={13} className="mns-phase-chevron" />
          }
        </button>

        {expanded && (
          <>
            {step.description && (
              <p className="mns-phase-desc">{step.description}</p>
            )}
            {step.pills.length > 0 && (
              <div className="mns-phase-pills">
                {step.pills.map(pill => (
                  <ActionPill key={pill.id} task={pill} lane={lane} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ─── Group flat tasks into phase steps ───────────────────────
function groupIntoPhases(tasks: AgentTask[]): PhaseStep[] {
  if (tasks.length === 0) return []

  // Each task becomes its own phase step (matching Manus pattern where each
  // "step" has a title, a description, and one or more action pills).
  // We cluster consecutive tasks with the same tool into one phase.
  const phases: PhaseStep[] = []
  let i = 0

  while (i < tasks.length) {
    const anchor = tasks[i]
    const sameTool: AgentTask[] = [anchor]

    // Collect subsequent tasks with same tool into the same phase
    while (i + 1 < tasks.length && tasks[i + 1].tool === anchor.tool) {
      i++
      sameTool.push(tasks[i])
    }

    const allDone = sameTool.every(t => t.status === 'done')
    const anyRunning = sameTool.some(t => t.status === 'running')
    const anyError = sameTool.some(t => t.status === 'error')
    const status = anyError ? 'error' : anyRunning ? 'running' : allDone ? 'done' : 'pending'

    // Generate a human-readable phase title from the first task label
    const title = generatePhaseTitle(anchor.label, anchor.tool ?? '')
    const description = generatePhaseDescription(anchor.tool ?? '', anchor.label, sameTool.length)

    phases.push({
      title,
      description,
      pills: sameTool,
      status,
      isLast: false,
    })

    i++
  }

  // Mark last phase
  if (phases.length > 0) {
    phases[phases.length - 1].isLast = true
  }

  return phases
}

function generatePhaseTitle(label: string, _tool: string): string {
  // Capitalize and clean up the label to use as a phase title
  const cleaned = label
    .replace(/^(run|execute|create|write|read|list|search|analyze)\s+/i, '')
    .replace(/\.(ts|tsx|js|jsx|py|json|md|css|html|sh)$/, '')
    .replace(/[-_]/g, ' ')
    .trim()

  // Capitalize first letter
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

function generatePhaseDescription(tool: string, label: string, count: number): string {
  const lbl = label.toLowerCase()

  if (tool === 'terminal') {
    if (lbl.includes('install') || lbl.includes('npm') || lbl.includes('pnpm')) {
      return 'Installerer avhengigheter og setter opp prosjektstrukturen.'
    }
    if (lbl.includes('zip') || lbl.includes('pack') || lbl.includes('archive')) {
      return 'Pakker filene for nedlasting.'
    }
    if (lbl.includes('test') || lbl.includes('run')) {
      return 'Kjører og verifiserer at koden fungerer som forventet.'
    }
    return 'Utfører kommandoer i terminalen for å fullføre oppgaven.'
  }

  if (tool === 'write_file') {
    if (count > 1) return `Skriver ${count} filer med nødvendig kode og konfigurasjon.`
    if (lbl.includes('config') || lbl.includes('settings')) {
      return 'Oppretter konfigurasjonsfil med riktige innstillinger.'
    }
    if (lbl.includes('readme') || lbl.includes('doc')) {
      return 'Skriver dokumentasjon med instruksjoner og forklaringer.'
    }
    return 'Skriver filen med implementasjonen.'
  }

  if (tool === 'read_file') {
    return 'Leser og analyserer eksisterende filer for å forstå konteksten.'
  }

  if (tool === 'web_search') {
    return 'Søker etter oppdatert informasjon og dokumentasjon.'
  }

  if (tool === 'run_code') {
    return 'Kjører koden og verifiserer at alt fungerer riktig.'
  }

  if (tool === 'list_files') {
    return 'Verifiserer filstrukturen og sjekker at alle filer er på plass.'
  }

  return 'Behandler oppgaven og forbereder neste steg.'
}

// ─── File card ────────────────────────────────────────────────
function FileCard({
  file,
  onOpen,
  onDownload,
}: {
  file: AgentFile
  onOpen?: () => void
  onDownload?: (e: React.MouseEvent) => void
}) {
  const [hovered, setHovered] = useState(false)
  const typeLabel =
    file.type === 'archive' ? 'Archive' :
    file.type === 'markdown' ? 'Markdown' :
    file.type === 'code' ? 'Code' :
    file.type === 'image' ? 'Image' : 'Document'

  return (
    <div
      className="manus-file-card"
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="manus-file-icon">{getFileIcon(file.type)}</div>
      <div className="manus-file-info">
        <div className="manus-file-name">{file.name}</div>
        <div className="manus-file-meta">{typeLabel}{file.size ? ` · ${file.size}` : ''}</div>
      </div>
      {hovered && onDownload && (
        <button className="manus-file-download" onClick={onDownload} title="Last ned">
          <Download size={13} />
          <span>Last ned</span>
        </button>
      )}
    </div>
  )
}

function StructuredOutputCard({ output }: { output: AgentStructuredOutput }) {
  const kindLabel: Record<AgentStructuredOutput['kind'], string> = {
    briefing: 'Briefing',
    report: 'Rapport',
    incident: 'Incident',
    coordination: 'Koordinering',
  }
  const kindMeta: Record<AgentStructuredOutput['kind'], { accent: string; border: string; bg: string; subtitle: string }> = {
    briefing: {
      accent: '#93C5FD',
      border: 'rgba(96,165,250,0.18)',
      bg: 'rgba(96,165,250,0.05)',
      subtitle: 'Kort, beslutningsklar oppsummering av det viktigste.',
    },
    report: {
      accent: '#A7F3D0',
      border: 'rgba(16,185,129,0.18)',
      bg: 'rgba(16,185,129,0.05)',
      subtitle: 'Tall, observasjoner og neste anbefalte trekk.',
    },
    incident: {
      accent: '#FCA5A5',
      border: 'rgba(248,113,113,0.2)',
      bg: 'rgba(248,113,113,0.05)',
      subtitle: 'Fakta, sannsynlig årsak og operasjonell oppfølging.',
    },
  }
  if (!kindMeta.coordination) {
    kindMeta.coordination = {
      accent: '#C4B5FD',
      border: 'rgba(139,92,246,0.18)',
      bg: 'rgba(139,92,246,0.05)',
      subtitle: 'Konkret plan for handoff, personer, kanaler og neste handling.',
    }
  }
  const meta = kindMeta[output.kind]

  return (
    <div style={{
      marginBottom: 16,
      padding: '14px 14px 12px',
      borderRadius: 14,
      border: `1px solid ${meta.border}`,
      background: meta.bg,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: output.headline ? 8 : 10 }}>
        <span style={{
          fontSize: 11,
          color: meta.accent,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 999,
          padding: '4px 8px',
          fontWeight: 600,
          letterSpacing: '0.02em',
        }}>
          {kindLabel[output.kind]}
        </span>
      </div>

      <div style={{ fontSize: 12, color: '#A3A3A3', lineHeight: 1.55, marginBottom: output.headline || output.summary ? 10 : 12 }}>
        {meta.subtitle}
      </div>

      {output.headline && (
        <div style={{ fontSize: 15, color: '#F3F4F6', fontWeight: 600, lineHeight: 1.45, marginBottom: output.summary ? 8 : 12 }}>
          {output.headline}
        </div>
      )}

      {output.summary && (
        <div style={{ fontSize: 13, color: '#CFCFCF', lineHeight: 1.6, marginBottom: output.sections.length > 0 ? 12 : 0 }}>
          {output.summary}
        </div>
      )}

        {output.sections.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {output.sections.map((section, index) => (
            <div key={`${section.title}-${index}`}>
              <div style={{ fontSize: 13, color: '#F3F4F6', fontWeight: 600, marginBottom: section.items?.length || section.body ? 6 : 0 }}>
                {section.title}
              </div>
              {section.body && (
                <div style={{ fontSize: 13, color: '#D1D5DB', lineHeight: 1.6, marginBottom: section.items?.length ? 6 : 0 }}>
                  {section.body}
                </div>
              )}
              {section.items && section.items.length > 0 && (
                <ul style={{ paddingLeft: '1.1rem', margin: 0, color: '#D1D5DB' }}>
                  {section.items.map((item, itemIndex) => (
                    <li key={`${section.title}-${itemIndex}`} style={{ marginBottom: 4, lineHeight: 1.55 }}>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          </div>
        )}

        {output.delivery && (
          <div style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}>
            <div style={{ fontSize: 13, color: '#F3F4F6', fontWeight: 600, marginBottom: 6 }}>
              Leveranse
            </div>
            <div style={{ fontSize: 13, color: '#D1D5DB', lineHeight: 1.6 }}>
              {output.delivery.summary}
            </div>
            {(output.delivery.channels?.length || output.delivery.artifacts?.length) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {output.delivery.channels?.map((channel, index) => (
                  <span
                    key={`delivery-channel-${index}-${channel}`}
                    style={{
                      fontSize: 11,
                      color: '#D1D5DB',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 999,
                      padding: '4px 8px',
                    }}
                  >
                    {channel}
                  </span>
                ))}
                {output.delivery.artifacts?.map((artifact, index) => (
                  <span
                    key={`delivery-artifact-${index}-${artifact}`}
                    style={{
                      fontSize: 11,
                      color: '#C4B5FD',
                      background: 'rgba(139,92,246,0.08)',
                      border: '1px solid rgba(139,92,246,0.12)',
                      borderRadius: 999,
                      padding: '4px 8px',
                    }}
                  >
                    {artifact}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {output.nextStep && (
          <div style={{
            marginTop: 12,
            paddingTop: 12,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          fontSize: 13,
          color: '#E5E7EB',
          lineHeight: 1.6,
        }}>
            <strong style={{ color: '#F3F4F6', fontWeight: 600 }}>Neste steg:</strong> {output.nextStep}
          </div>
        )}

        {output.followUpActions && output.followUpActions.length > 0 && (
          <div style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}>
            <div style={{ fontSize: 13, color: '#F3F4F6', fontWeight: 600, marginBottom: 6 }}>
              Kan gjøre videre
            </div>
            <ul style={{ paddingLeft: '1.1rem', margin: 0, color: '#D1D5DB' }}>
              {output.followUpActions.map((item, index) => (
                <li key={`follow-up-${index}`} style={{ marginBottom: 4, lineHeight: 1.55 }}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {output.openQuestions && output.openQuestions.length > 0 && (
          <div style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}>
            <div style={{ fontSize: 13, color: '#F3F4F6', fontWeight: 600, marginBottom: 6 }}>
              Avklaringer
            </div>
            <ul style={{ paddingLeft: '1.1rem', margin: 0, color: '#D1D5DB' }}>
              {output.openQuestions.map((item, index) => (
                <li key={`open-question-${index}`} style={{ marginBottom: 4, lineHeight: 1.55 }}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

function getLaneSourceMeta(lane?: string, responseMode?: string): { title: string; subtitle: string; uppercase: boolean } {
  if (lane === 'briefing') {
    return {
      title: 'Briefing-kilder',
      subtitle: 'Sine henter inn akkurat nok kontekst til å gi en skarp briefing.',
      uppercase: false,
    }
  }
  if (lane === 'report') {
    return {
      title: 'Rapportgrunnlag',
      subtitle: 'Sine samler kildene som brukes til tall, observasjoner og leveranse.',
      uppercase: false,
    }
  }
  if (lane === 'incident') {
    return {
      title: 'Incident-signaler',
      subtitle: 'Sine samler spor fra systemene som forklarer hva som skjedde.',
      uppercase: false,
    }
  }
  if (lane === 'monitoring' || responseMode === 'monitoring') {
    return {
      title: 'Monitor-oppsett',
      subtitle: 'Sine peker ut hvilke signaler og systemer monitoren bygger på.',
      uppercase: true,
    }
  }
  if (lane === 'coordination') {
    return {
      title: 'Koordineringskilder',
      subtitle: 'Sine bruker disse systemene for å få personer, tidspunkt og oppfølging til å henge sammen.',
      uppercase: false,
    }
  }
  return {
    title: 'Kilder brukt',
    subtitle: 'Sine viser hvilke systemer som faktisk ble brukt i denne kjøringen.',
    uppercase: false,
  }
}

function RuntimeSummaryCard({
  memorySummary,
  workflowSummary,
}: {
  memorySummary?: AgentMemorySummary
  workflowSummary?: AgentWorkflowSummary
}) {
  if (!memorySummary && !workflowSummary) return null

  return (
    <div style={{
      marginBottom: 16,
      padding: '14px 14px 12px',
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.08)',
      background: 'rgba(255,255,255,0.025)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 10,
      }}>
        <div style={{ fontSize: 12, color: '#D1D5DB', fontWeight: 600, letterSpacing: '0.02em' }}>
          Runtime-sammendrag
        </div>
        <div style={{ fontSize: 11, color: '#6B7280' }}>
          Minne og workflow
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {memorySummary && (
          <div style={{
            padding: '10px 11px',
            borderRadius: 12,
            border: '1px solid rgba(96,165,250,0.18)',
            background: 'rgba(96,165,250,0.06)',
          }}>
            <div style={{ fontSize: 12, color: '#93C5FD', fontWeight: 600, marginBottom: 6 }}>
              Minne
            </div>
            <div style={{ fontSize: 13, color: '#E5E7EB', lineHeight: 1.55 }}>
              {memorySummary.summary || 'Sine brukte eller oppdaterte minnekontekst i denne kjøringen.'}
            </div>
          </div>
        )}

        {workflowSummary && (
          <div style={{
            padding: '10px 11px',
            borderRadius: 12,
            border: '1px solid rgba(16,185,129,0.18)',
            background: 'rgba(16,185,129,0.05)',
          }}>
            <div style={{ fontSize: 12, color: '#A7F3D0', fontWeight: 600, marginBottom: 6 }}>
              Workflow
            </div>
            <div style={{ fontSize: 13, color: '#E5E7EB', lineHeight: 1.55, marginBottom: 6 }}>
              {workflowSummary.summary || [
                workflowSummary.kind && `Type: ${workflowSummary.kind}`,
                workflowSummary.lane && `Lane: ${workflowSummary.lane}`,
                workflowSummary.runner_style && `Stil: ${workflowSummary.runner_style}`,
              ].filter(Boolean).join(' • ') || 'Sine oppdaterte workflow-kontekst for denne kjøringen.'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {workflowSummary.status && (
                <div style={{ fontSize: 12, color: '#9CA3AF', lineHeight: 1.5 }}>
                  Status: {workflowSummary.status}
                </div>
              )}
              {workflowSummary.response_mode && (
                <div style={{ fontSize: 12, color: '#9CA3AF', lineHeight: 1.5 }}>
                  Responsmodus: {workflowSummary.response_mode}
                </div>
              )}
              {workflowSummary.next_step && (
                <div style={{ fontSize: 12, color: '#9CA3AF', lineHeight: 1.5 }}>
                  Neste steg: {workflowSummary.next_step}
                </div>
              )}
              {workflowSummary.channels && workflowSummary.channels.length > 0 && (
                <div style={{ fontSize: 12, color: '#9CA3AF', lineHeight: 1.5 }}>
                  Kanaler: {workflowSummary.channels.slice(0, 3).join(', ')}
                </div>
              )}
              {workflowSummary.artifacts && workflowSummary.artifacts.length > 0 && (
                <div style={{ fontSize: 12, color: '#9CA3AF', lineHeight: 1.5 }}>
                  Artefakter: {workflowSummary.artifacts.slice(0, 3).join(', ')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main AgentChatMessage ────────────────────────────────────
export function AgentChatMessage({ message, liveLogs, planningTasks, onOpenFile, onSuggestion }: AgentChatMessageProps) {
  const tasks = message.agentTasks ?? []
  const files = message.agentFiles ?? []
  const sourcePack = message.agentSourcePack ?? []
  const intentDecision = message.agentIntentDecision
  const responseMode = message.agentResponseMode ?? 'default'
  const lane = intentDecision?.lane
  const sourceMeta = getLaneSourceMeta(lane, responseMode)
  const selectedCapabilities = message.agentSelectedCapabilities ?? []
  const structuredOutput = message.agentStructuredOutput
  const memorySummary = message.agentMemorySummary
  const workflowSummary = message.agentWorkflowSummary
  const semanticEvents = message.agentSemanticEvents ?? []
  const subagentRuntime = message.agentSubagentRuntime ?? []
  const recurringProposal = message.agentRecurringProposal
  const suggestions = message.agentSuggestions ?? []
  const rawStatus = message.agentStatus ?? 'running'
  const displayContent = cleanVisibleAgentContent(message.content)
  const status = rawStatus
  const isCompleted = status === 'completed'
  const isCompletedWithWarnings = status === 'completed_with_warnings'
  const isFatalFailed = status === 'fatal_failed'
  const isFailed = status === 'failed' || isFatalFailed || status === 'interrupted' || status === 'resume_available'
  const isFinished = isCompleted || isCompletedWithWarnings
  const isRunning = !isCompleted && !isCompletedWithWarnings && !isFailed && status !== 'stopped' && status !== 'interrupted' && status !== 'resume_available'
  const hasPlanningSequence = Boolean(planningTasks && planningTasks.length > 0)
  const showDetailedExecution = !hasPlanningSequence && tasks.length > 0 && (isRunning || isFinished)
  const dedupedSourcePack = sourcePack.filter((source, index, arr) => {
    const key = `${source.app}::${source.summary}::${source.label}`
    return arr.findIndex(item => `${item.app}::${item.summary}::${item.label}` === key) === index
  })
  const successfulSources = dedupedSourcePack.filter(source => source.status !== 'failed')
  const visibleSourcePack = (
    successfulSources.length > 0 ? successfulSources : dedupedSourcePack
  ).slice(0, showDetailedExecution ? 8 : 4)

  // Derive current live status text from the most recent log entry
  const currentStatusText = (() => {
    if (!liveLogs || liveLogs.length === 0) return null
    const last = liveLogs[liveLogs.length - 1]
    return getLaneAwareLiveStatus(last, lane) || null
  })()

  const activePlanningTask = planningTasks?.find(task => task.status === 'active')
    ?? planningTasks?.find(task => task.status === 'pending')
  const latestNaturalUpdate = (() => {
    const fromTask = activePlanningTask?.prose?.trim()
    if (fromTask) return fromTask
    const latestThinking = [...(liveLogs ?? [])]
      .reverse()
      .find(entry => entry.type === 'thinking' && entry.message.trim().length > 0)
    return latestThinking?.message?.trim() ?? null
  })()
  const fallbackNextActions = isFinished ? [
    isCompletedWithWarnings
      ? 'Fortsett fra dette punktet og fullfør det som mangler.'
      : 'Lag en mer polert versjon av leveransen.',
    files.length > 0
      ? 'Konverter leveransen til PDF eller PowerPoint.'
      : 'Lag en konkret leveransefil med funnene.',
    'Kjør en ekstra kvalitetssjekk av kilder, tall og forbehold.',
  ] : []
  const suggestedNextActions = Array.from(new Set([
    ...suggestions,
    ...(structuredOutput?.followUpActions ?? []),
    structuredOutput?.nextStep,
    ...fallbackNextActions,
  ].filter((item): item is string => Boolean(item && item.trim())))).slice(0, 3)

  const phases = groupIntoPhases(tasks)
  const showSourceContext = !hasPlanningSequence
    && !structuredOutput
    && !isRunning
    && !isFailed
    && (visibleSourcePack.length > 0 || intentDecision)

  const getSourceIcon = (app: string) => {
    const normalized = app.toLowerCase()
    if (normalized.includes('gmail') || normalized.includes('mail') || normalized.includes('slack')) return <MessageSquare size={12} />
    if (normalized.includes('calendar')) return <CalendarDays size={12} />
    if (normalized.includes('github')) return <Code2 size={12} />
    if (normalized.includes('sheet') || normalized.includes('analytics') || normalized.includes('mixpanel')) return <BarChart3 size={12} />
    if (normalized.includes('drive') || normalized.includes('notion')) return <FileText size={12} />
    if (normalized.includes('browser') || normalized.includes('web') || normalized.includes('search')) return <Globe size={12} />
    return <Zap size={12} />
  }

  const laneLabel = (lane?: string) => {
    const labels: Record<string, string> = {
      default: 'Generell agent',
      briefing: 'Briefing',
      report: 'Rapport',
      coordination: 'Koordinering',
      monitoring: 'Monitorering',
      incident: 'Incident',
    }
    return labels[lane ?? 'default'] ?? lane ?? 'Generell agent'
  }

  const handleDownload = (file: AgentFile, e: React.MouseEvent) => {
    e.stopPropagation()
    if (file.content) {
      const blob = new Blob([file.content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.name
      a.click()
      URL.revokeObjectURL(url)
    } else if (file.downloadUrl) {
      const a = document.createElement('a')
      a.href = file.downloadUrl
      a.download = file.name
      a.click()
    }
  }

  return (
    <div className="message-assistant animate-fade-in">
      {/* Avatar row */}
      <div className="message-avatar-row">
        <img src="/Sinev6.svg" alt="Sine" className="message-logo-img" />
      </div>

      {/* Content */}
      <div className="message-body" style={{ flex: 1, minWidth: 0 }}>

        {showSourceContext && (
            <div style={{ marginBottom: 14 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 8,
              color: '#9CA3AF',
              fontSize: 12,
              letterSpacing: '0.02em',
              textTransform: sourceMeta.uppercase ? 'uppercase' : 'none',
            }}>
              <span>{sourceMeta.title}</span>
              {intentDecision && (
                <span style={{
                  fontSize: 11,
                  color: '#D1D5DB',
                  background: 'rgba(96,165,250,0.12)',
                  border: '1px solid rgba(96,165,250,0.18)',
                  borderRadius: 999,
                  padding: '3px 8px',
                  textTransform: 'none',
                }}>
                  {laneLabel(intentDecision.lane)}
                </span>
              )}
            </div>
            <div style={{ color: '#8B8B8B', fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>
              {sourceMeta.subtitle}
            </div>
              {intentDecision?.rationale && (
                <div style={{ color: '#8B8B8B', fontSize: 12, lineHeight: 1.5, marginBottom: visibleSourcePack.length > 0 ? 8 : 0 }}>
                  {intentDecision.rationale}
                  {selectedCapabilities.length > 0 && (
                    <span style={{ color: '#6B7280' }}>
                      {' '}Prioriterer {selectedCapabilities.slice(0, 3).map(item => item.name).join(', ')}.
                    </span>
                  )}
                </div>
              )}
            {visibleSourcePack.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
               {visibleSourcePack.map((source, index) => (
                  <div
                    key={`${source.app}-${index}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    color: source.status === 'failed' ? '#FCA5A5' : '#D1D5DB',
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.06)',
                    color: source.status === 'failed' ? '#FCA5A5' : '#93C5FD',
                    flexShrink: 0,
                  }}>
                    {getSourceIcon(source.app)}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <strong style={{ color: '#F3F4F6', fontWeight: 600 }}>{source.app}</strong>
                    <span style={{ color: '#9CA3AF' }}> · {source.action}</span>
                    <div style={{ color: '#D1D5DB' }}>{source.summary}</div>
                  </div>
                </div>
              ))}
                </div>
              )}
            </div>
          )}

        {recurringProposal && (
          <div style={{ marginBottom: 16 }}>
            <RecurringTaskCard proposal={recurringProposal} compact />
          </div>
        )}

        {hasPlanningSequence && (
          <ManusPlanningTasksCard tasks={planningTasks ?? []} lane={lane} />
        )}

        {subagentRuntime.length > 0 && (
          <SubagentRuntimePanel agents={subagentRuntime} running={isRunning} />
        )}

        {structuredOutput && !displayContent.trim() && (
          <StructuredOutputCard output={structuredOutput} />
        )}

        {!isFailed && (memorySummary || workflowSummary) && (
          <RuntimeSummaryCard
            memorySummary={memorySummary}
            workflowSummary={workflowSummary}
          />
        )}

        {/* Intro text with markdown */}
        {displayContent && (
          <div className="manus-agent-text">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a({ href, children }) {
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer"
                      style={{ color: '#60A5FA', textDecoration: 'underline', cursor: 'pointer' }}>
                      {children}
                    </a>
                  )
                },
                h1({ children }) {
                  return <h1 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0.75rem 0 0.4rem', color: '#F3F4F6' }}>{children}</h1>
                },
                h2({ children }) {
                  return <h2 style={{ fontSize: '1.05rem', fontWeight: 600, margin: '0.65rem 0 0.35rem', color: '#F3F4F6' }}>{children}</h2>
                },
                h3({ children }) {
                  return <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0.55rem 0 0.3rem', color: '#E5E7EB' }}>{children}</h3>
                },
                p({ children }) {
                  return <p style={{ margin: '0 0 0.55rem 0', lineHeight: 1.65 }}>{children}</p>
                },
                strong({ children }) {
                  return <strong style={{ fontWeight: 600, color: '#F3F4F6' }}>{children}</strong>
                },
                code({ className, children }) {
                  const match = /language-(\w+)/.exec(className || '')
                  if (match) return <code className={className}>{children}</code>
                  return (
                    <code style={{
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: 4,
                      padding: '1px 5px',
                      fontSize: '0.8em',
                      fontFamily: 'monospace',
                      color: '#E2E8F0',
                    }}>
                      {children}
                    </code>
                  )
                },
                ul({ children }) {
                  return <ul style={{ paddingLeft: '1.2rem', margin: '0.3rem 0 0.55rem 0' }}>{children}</ul>
                },
                ol({ children }) {
                  return <ol style={{ paddingLeft: '1.2rem', margin: '0.3rem 0 0.55rem 0' }}>{children}</ol>
                },
                li({ children }) {
                  return <li style={{ marginBottom: '0.2rem', lineHeight: 1.6 }}>{children}</li>
                },
                // Suppress horizontal rules — they make responses look overly AI-formatted
                hr() {
                  return null
                },
                // Table rendering — clean, readable
                table({ children }) {
                  return (
                    <div style={{ overflowX: 'auto', margin: '0.6rem 0' }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem' }}>{children}</table>
                    </div>
                  )
                },
                thead({ children }) {
                  return <thead style={{ borderBottom: '1px solid #374151' }}>{children}</thead>
                },
                th({ children }) {
                  return <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#F3F4F6', whiteSpace: 'nowrap' }}>{children}</th>
                },
                td({ children }) {
                  return <td style={{ padding: '6px 12px', borderBottom: '1px solid #1F2937', color: '#D1D5DB', verticalAlign: 'top' }}>{children}</td>
                },
              }}
            >
              {displayContent}
            </ReactMarkdown>
          </div>
        )}

        {/* Live thinking stream — shown while agent is running with no tasks yet */}
        {!displayContent.trim() && !hasPlanningSequence && semanticEvents.length > 1 && (
          <SemanticEventStream events={semanticEvents} running={isRunning} />
        )}

        {isRunning && !hasPlanningSequence && (
          <div className="sns-thinking-bubble" style={{ marginTop: displayContent ? 10 : 0, marginBottom: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <SineThinkingLoader size="sm" label="Tenker" />
              {(latestNaturalUpdate || currentStatusText || activePlanningTask?.label) && (
                <span style={{ color: '#A3A3A3', fontSize: 13, lineHeight: 1.55 }}>
                  {latestNaturalUpdate ?? currentStatusText ?? activePlanningTask?.label}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Phase steps stream — Manus style */}
          {phases.length > 0 && showDetailedExecution && (
            <div className="mns-phases-stream">
              {phases.map((phase, i) => (
                <PhaseStepView key={i} step={phase} lane={lane} />
              ))}
          </div>
        )}

        {/* Delivery section */}
        {isFinished && files.length > 0 && (
          <div className="manus-delivery-section">
            <div className="manus-delivery-intro">
              <strong>Leveranse</strong>
              <span>{files.length} fil{files.length === 1 ? '' : 'er'} er klare for åpning eller nedlasting.</span>
            </div>
            <div className="manus-files-grid">
              {files.slice(0, 3).map((file, i) => (
                <FileCard
                  key={i}
                  file={file}
                  onOpen={() => onOpenFile?.(file, files)}
                  onDownload={(e) => handleDownload(file, e)}
                />
              ))}
              {files.length > 3 && (
                <button
                  className="manus-view-all-btn"
                  onClick={() => onOpenFile?.(files[0], files)}
                >
                  <File size={14} />
                  <span>Se alle filer i denne oppgaven</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Completed badge */}
        {isFinished && (tasks.length > 0 || files.length > 0 || structuredOutput) && (
          <div className="manus-complete-row">
            <div className="manus-complete-badge">
              <CheckCircle2 size={13} />
              <span className="manus-complete-badge-label">{isCompletedWithWarnings ? 'Oppgave levert med forbehold' : 'Oppgave fullført'}</span>
              <span>Oppgave fullført</span>
            </div>
            <div className="manus-rating">
              <span>Hvordan var resultatet?</span>
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} className="manus-star">★</button>
              ))}
            </div>
          </div>
        )}

        {isCompletedWithWarnings && (
          <div className="manus-continue-block">
            Jeg kan fortsette fra dette punktet uten å starte på nytt. Den tryggeste neste handlingen er å fullføre manglende kilder, tall eller filpakking.
          </div>
        )}

        {isFailed && (
          <div className="manus-failed-badge">
            <XCircle size={13} />
            <span>Oppgave mislyktes</span>
          </div>
        )}

        {/* Suggestions */}
        {isFinished && suggestedNextActions.length > 0 && (
          <div className="manus-suggestions">
            <p className="manus-suggestions-label">Foreslåtte oppfølginger</p>
            {suggestedNextActions.map((s, i) => (
              <button
                key={i}
                className="manus-suggestion-row"
                onClick={() => onSuggestion?.(s)}
              >
                <MessageSquare size={13} className="manus-suggestion-icon" />
                <span>{s.replace(/\*\*/g, '').replace(/^#+\s*/, '').trim()}</span>
                <ChevronRight size={13} className="manus-suggestion-arrow" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── File popup modal ─────────────────────────────────────────
interface FilePopupProps {
  file: AgentFile
  allFiles?: AgentFile[]
  onClose: () => void
  onDownload?: (file: AgentFile) => void
}

export function FilePopup({ file, allFiles = [], onClose, onDownload }: FilePopupProps) {
  const [selectedFile, setSelectedFile] = useState<AgentFile>(file)
  const isMarkdownFile = (f: AgentFile) => f.type === 'markdown' || /\.mdx?$/i.test(f.name)

  const handleDownload = (f: AgentFile) => {
    if (onDownload) { onDownload(f); return }
    if (f.content) {
      const blob = new Blob([f.content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = f.name
      a.click()
      URL.revokeObjectURL(url)
    } else if (f.downloadUrl) {
      const a = document.createElement('a')
      a.href = f.downloadUrl
      a.download = f.name
      a.click()
    }
  }

  const displayFiles = allFiles.length > 0 ? allFiles : [file]

  const renderContent = () => {
    if (!selectedFile.content) {
      return (
        <div style={{ padding: 32, color: '#6B7280', fontSize: 13, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <File size={32} style={{ opacity: 0.3 }} />
          <p style={{ margin: 0 }}>Innhold ikke lastet</p>
          <p style={{ fontSize: 11, margin: 0, color: '#4B5563' }}>Klikk Last ned for å hente filen</p>
        </div>
      )
    }
    if (selectedFile.type === 'image') {
      return (
        <img src={`data:image/*;base64,${selectedFile.content}`} alt={selectedFile.name}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: 16 }} />
      )
    }
    if (isMarkdownFile(selectedFile)) {
      return (
        <article className="file-popup-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {selectedFile.content}
          </ReactMarkdown>
        </article>
      )
    }
    return <pre className="file-popup-code">{selectedFile.content}</pre>
  }

  return (
    <div className="file-popup-overlay" onClick={onClose}>
      <div className="file-popup" onClick={e => e.stopPropagation()}>
        <div className="file-popup-header">
          <div className="file-popup-title">
            {getFileIcon(selectedFile.type)}
            <span>{selectedFile.name}</span>
          </div>
          <div className="file-popup-actions">
            <button className="file-popup-action-btn" onClick={() => handleDownload(selectedFile)} title="Last ned">
              <Download size={15} />
            </button>
            <button className="file-popup-action-btn" title="Åpne i ny fane"
              onClick={() => {
                if (selectedFile.content) {
                  const w = window.open('', '_blank')
                  if (w) {
                    w.document.write(`<pre style="font-family:monospace;padding:16px;background:#111;color:#e5e5e5;min-height:100vh;margin:0;white-space:pre-wrap">${selectedFile.content.replace(/</g, '&lt;')}</pre>`)
                  }
                }
              }}
            >
              <ExternalLink size={15} />
            </button>
            <button className="file-popup-action-btn file-popup-close" onClick={onClose} title="Lukk">✕</button>
          </div>
        </div>

        <div className="file-popup-body">
          {displayFiles.length > 1 && (
            <div className="file-popup-tree">
              <div className="file-popup-tree-root">
                <Archive size={12} />
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>Filer</span>
              </div>
              {displayFiles.map((f, i) => (
                <div
                  key={i}
                  className={`file-popup-tree-item${f.path === selectedFile.path ? ' active' : ''}`}
                  onClick={() => setSelectedFile(f)}
                >
                  {getFileIcon(f.type)}
                  <span>{f.name}</span>
                </div>
              ))}
            </div>
          )}
          <div className="file-popup-content">{renderContent()}</div>
        </div>
      </div>
    </div>
  )
}

// ─── Tool icon (exported) ─────────────────────────────────────
export function getToolIcon(tool: string) {
  return getToolMeta(tool).icon
}
